import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Provenance as ProvenanceRow } from "../api/types";
import { orAbsent, shortDate } from "../lib/format";
import { applySorts, useListParams } from "../lib/useListParams";
import { csvFilename, downloadCsv, toCsv } from "../lib/csv";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { Toolbar } from "../ui/Toolbar";
import { Spinner } from "./Spinner";

/* The sources, as a table of what EVIE learned about a record and when,
 * newest first. Every source, not the best one, because a rep asked "how did
 * you get my number" on a call should be able to give any of the answers,
 * and with the date first it also answers what is new here. A hand-made
 * correction appears too, because an edit is a fact with a human source. */
const CSV = ["source_name", "source_kind", "source_tier", "as_of", "as_of_kind", "link", "publisher_domain"];

const safe = (q: string) => q.replace(/[,()*\\]/g, " ").trim();

export function ActivityTable({ token, entityType, entityId }: { token: string; entityType: string; entityId: string }) {
  // In the URL under its own prefix, so a link carries the search and sort.
  const params = useListParams("as_of", [], false, "", "a.");
  const q = safe(params.search);
  const query = useQuery({
    queryKey: ["provenance", entityType, entityId, params.sorts, q],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let qq = apiClient(token)
        .from("provenance")
        .select("source_name,source_kind,source_tier,as_of,as_of_kind,link,link_kind,publisher_domain")
        .eq("entity_type", entityType).eq("entity_id", entityId);
      if (q) qq = qq.or(`source_name.ilike.*${q}*,publisher_domain.ilike.*${q}*`);
      const { data, error } = await applySorts(qq, params.sorts).limit(200);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ProvenanceRow[];
    },
  });
  const rows = query.data ?? [];
  const columns: ColumnSpec<ProvenanceRow>[] = [
    { id: "as_of", header: "Date", role: "leading", fields: ["as_of"], sort: "as_of", sortDescendingFirst: true, narrowLabel: null,
      cell: (r) => <>{shortDate(r.as_of)}{r.as_of_kind === "observed" ? <span className="muted" title="The date we fetched it"> (fetched)</span> : null}</> },
    { id: "source", header: "Source", role: "leading", fields: ["source_name"], sort: "source_name", title: true, narrowLabel: null,
      cell: (r) => orAbsent(r.source_name) },
    { id: "tier", header: "Tier", role: "secondary", fields: ["source_tier"], sort: "source_tier", narrowLabel: null, cell: (r) => orAbsent(r.source_tier) },
    { id: "kind", header: "Type", role: "secondary", fields: ["source_kind"], sort: "source_kind", narrowLabel: null, cell: (r) => orAbsent(r.source_kind) },
    { id: "page", header: "Page", role: "secondary", fields: ["link"], narrowLabel: null,
      cell: (r) => r.link ? <a href={r.link} target="_blank" rel="noreferrer">open the page</a> : <span className="muted" title="No page recorded">no page</span> },
  ];
  return (
    <section className="rp-tabbody" aria-label="Activity">
      <Toolbar
        id="activity"
        search={{ label: "Search by source", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        onClearAll={() => params.set({ search: "" })}
        right={rows.length ? (
          <Button size="compact-sm" onClick={() => downloadCsv(csvFilename("sources"),
            toCsv(CSV, rows as unknown as Record<string, unknown>[]))}>
            CSV
          </Button>
        ) : null}
      />
      <p className="muted rp-count">Every source that asserted something about this record.</p>
      {query.isLoading ? <p className="muted"><Spinner />Loading sources.</p>
        : query.isError ? <p className="refusal">{(query.error as Error).message}</p>
        : !rows.length ? (
          <p className="muted">
            {q ? "No source matches that search." : "No source is recorded against this record. That is unusual rather than impossible, and it means the facts here cannot be traced to where they came from."}
          </p>
        ) : <DataTable columns={columns} rows={rows} rowKey={(r) => `${r.source_name}-${r.as_of}-${r.link ?? ""}`} params={params} />}
    </section>
  );
}
